import moment from 'moment'
import { useCallback, useState } from 'react'
import type { Booking } from 'repositories/BerthApi/types/booking'
import type { PreviousBooking } from 'repositories/BerthApi/getPreviousBooking/type'

import type { SnackbarPayload } from 'components/Snackbar/useSnackbar'
import { BOOKING_STATUS, SHORTEN_TIME_FLAG } from 'constants/Booking'
import { getApiErrorMessage } from 'containers/SpBerthManagement/utils/berthManagementHelpers'

import { useCallingReception } from './useCallingReception'
import { useCheckPreviousBooking } from './useCheckPreviousBooking'

type CallingState = {
  booking: Booking
} | null

type ForceFinishState = {
  previousBooking: PreviousBooking
  booking: Booking
  bookingMinute: number
  berthId: string
} | null

type Args = {
  date: string
  tenantId: string | null
  onNotify: (payload: SnackbarPayload) => void
}

type Result = {
  callingTarget: CallingState
  forceFinish: ForceFinishState
  isDialogLoading: boolean
  handleCallingStart: (booking: Booking) => void
  handleCallingOk: (bookingMinute: number, dialogBerthId: string) => Promise<void>
  handleCallingCancel: () => void
  handleForceFinishOk: () => void
  handleForceFinishCancel: () => void
}

const ACTIVE_STATUSES = [BOOKING_STATUS.Arrival, BOOKING_STATUS.Calling, BOOKING_STATUS.Working]

export const useCallingAction = ({ date, tenantId, onNotify }: Args): Result => {
  const [callingTarget, setCallingTarget] = useState<CallingState>(null)
  const [forceFinish, setForceFinish] = useState<ForceFinishState>(null)

  const { mutate: mutateCallingReception, isPending: isCallingReceptionPending } =
    useCallingReception()
  const { mutateAsync: mutateAsyncCheckPreviousBooking, isPending: isCheckPreviousBookingPending } =
    useCheckPreviousBooking()

  const handleCallingStart = useCallback((booking: Booking) => {
    setCallingTarget({ booking })
  }, [])

  const executeCallingReception = useCallback(
    (booking: Booking, bookingMinute: number, dialogBerthId: string, prevBookingId?: string) => {
      if (!tenantId || !dialogBerthId || !booking.bookingId) return

      mutateCallingReception(
        {
          bookingId: booking.bookingId,
          tenantId,
          berthId: dialogBerthId,
          bookingMinute,
          ...(prevBookingId && {
            prevBookingId,
            shortenTimeFlag: SHORTEN_TIME_FLAG.ON,
          }),
        },
        {
          onSuccess: () => {
            setCallingTarget(null)
            onNotify({ message: 'ステータス更新に成功しました。', variant: 'success' })
          },
          onError: (error: unknown) => {
            setCallingTarget(null)
            onNotify({ message: getApiErrorMessage(error), variant: 'error' })
          },
        }
      )
    },
    [tenantId, mutateCallingReception, onNotify]
  )

  const handleCallingOk = useCallback(
    async (bookingMinute: number, dialogBerthId: string) => {
      if (!callingTarget || !tenantId) return

      const { booking } = callingTarget

      try {
        const result = await mutateAsyncCheckPreviousBooking({
          bookingId: booking.bookingId!,
          berthId: dialogBerthId,
          startDate: date.replace(/-/g, ''),
          startTime: moment().format('HH:mm:ss'),
        })

        if (result?.canComplete === false) {
          setCallingTarget(null)
          onNotify({
            message: result.validationMessage || 'ステータス更新に失敗しました。',
            variant: 'error',
          })
          return
        }

        const prevBooking = result?.previousBooking

        if (prevBooking && prevBooking.bookingId && prevBooking.status != null) {
          if (ACTIVE_STATUSES.includes(prevBooking.status as (typeof ACTIVE_STATUSES)[number])) {
            setCallingTarget(null)
            setForceFinish({
              previousBooking: prevBooking,
              booking,
              bookingMinute,
              berthId: dialogBerthId,
            })
            return
          }

          executeCallingReception(booking, bookingMinute, dialogBerthId, prevBooking.bookingId)
          return
        }
      } catch (error) {
        console.error('getLatestInPastByTime failed, proceeding without prev booking check:', error)
      }

      executeCallingReception(booking, bookingMinute, dialogBerthId)
    },
    [
      callingTarget,
      tenantId,
      date,
      executeCallingReception,
      mutateAsyncCheckPreviousBooking,
      onNotify,
    ]
  )

  const handleCallingCancel = useCallback(() => {
    setCallingTarget(null)
  }, [])

  const handleForceFinishOk = useCallback(() => {
    if (!forceFinish) return

    const { previousBooking, booking, bookingMinute, berthId: ffBerthId } = forceFinish
    setForceFinish(null)
    executeCallingReception(booking, bookingMinute, ffBerthId, previousBooking.bookingId!)
  }, [forceFinish, executeCallingReception])

  const handleForceFinishCancel = useCallback(() => {
    setForceFinish(null)
  }, [])

  const isDialogLoading = isCallingReceptionPending || isCheckPreviousBookingPending

  return {
    callingTarget,
    forceFinish,
    isDialogLoading,
    handleCallingStart,
    handleCallingOk,
    handleCallingCancel,
    handleForceFinishOk,
    handleForceFinishCancel,
  }
}
